import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Wrench, Plus, RefreshCw, Download, Upload, FileUp, ChevronRight, AlertTriangle, CheckCircle2, Clock, PowerOff, Building2, X as XIcon, Trash2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { API, api, endpoints, getTenantScope } from "@/lib/api";
import { toast } from "sonner";

const STATUS_TONE = {
  "Current": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Due Soon": "bg-amber-50 text-amber-700 border-amber-200",
  "Overdue": "bg-rose-50 text-rose-700 border-rose-200",
  "Out of Service": "bg-slate-100 text-slate-700 border-slate-200",
};
const REQ_TYPES = ["Calibration", "Load Test", "Service", "Inspection", "Planned Maintenance", "Pressure Test", "Certification", "Other"];
const fmt = (s) => s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—";

async function apiGet(p) { const r = await api.get(p); return r.data; }
async function apiPost(p, b) { const r = await api.post(p, b); return r.data; }
async function apiPostForm(p, fd) { const r = await api.post(p, fd, { headers: { "Content-Type": "multipart/form-data" } }); return r.data; }

export default function EquipmentPage() {
  const scope = getTenantScope();
  const [params] = useSearchParams();
  const qp = params.get("client");
  const [clientSlug, setClientSlug] = useState(qp || scope?.slug || "northwind-corp");
  const [clients, setClients] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [filters, setFilters] = useState({ status: "all", requirement_type: "all", equipment_type: "all", department: "all" });
  const [openAdd, setOpenAdd] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const [openRecord, setOpenRecord] = useState(false);
  const [prefillRecord, setPrefillRecord] = useState({});
  const isClient = ["client", "client_admin", "client_employee"].includes(scope?.role);

  const load = () => {
    endpoints.listClients().then(setClients).catch(() => {});
    apiGet(`/equipment?client_slug=${clientSlug}`).then(setEquipment).catch(() => setEquipment([]));
    apiGet(`/equipment-dashboard?client_slug=${clientSlug}`).then(setDashboard).catch(() => setDashboard(null));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientSlug]);

  const filtered = useMemo(() => equipment.filter(e => {
    if (filters.status !== "all" && e.status !== filters.status) return false;
    if (filters.equipment_type !== "all" && e.equipment_type !== filters.equipment_type) return false;
    if (filters.department !== "all" && e.department !== filters.department) return false;
    if (filters.requirement_type !== "all" && !(e.requirements || []).some(r => r.requirement_type === filters.requirement_type)) return false;
    return true;
  }), [equipment, filters]);

  const types = useMemo(() => Array.from(new Set(equipment.map(e => e.equipment_type).filter(Boolean))), [equipment]);
  const depts = useMemo(() => Array.from(new Set(equipment.map(e => e.department).filter(Boolean))), [equipment]);

  const openRecordFor = (eq, requirement_type = "") => {
    setPrefillRecord({ eq, requirement_type });
    setOpenRecord(true);
  };

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 text-white flex items-center justify-center shadow-md">
            <Wrench className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Equipment, Calibration & Maintenance</h1>
            <p className="text-xs text-slate-500">Register, requirements, certificates and due-date tracking for every asset.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isClient && (
            <Select value={clientSlug} onValueChange={setClientSlug}>
              <SelectTrigger className="w-48" data-testid="eq-client-select"><SelectValue /></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.slug} value={c.slug}>{c.company_name}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="eq-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
          <a href={`${API}/equipment-export.csv?client_slug=${clientSlug}`} className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm hover:bg-slate-50" data-testid="eq-export">
            <Download className="h-4 w-4" /> Export register
          </a>
          <Button data-testid="eq-add-button" className="brand-gradient text-white shadow" onClick={() => setOpenAdd(true)}>
            <Plus className="h-4 w-4 mr-1" /> Add Equipment
          </Button>
        </div>
      </div>

      {/* Stat grid — clickable filters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
        <Stat label="Total" value={dashboard?.counts?.total ?? 0} tone="indigo" onClick={() => setFilters({ ...filters, status: "all" })} testid="eq-stat-total" />
        <Stat label="Current" value={dashboard?.counts?.Current ?? 0} tone="emerald" onClick={() => setFilters({ ...filters, status: "Current" })} testid="eq-stat-current" />
        <Stat label="Due Soon (30d)" value={dashboard?.counts?.["Due Soon"] ?? 0} tone="amber" onClick={() => setFilters({ ...filters, status: "Due Soon" })} testid="eq-stat-due" />
        <Stat label="Overdue" value={dashboard?.counts?.Overdue ?? 0} tone="rose" onClick={() => setFilters({ ...filters, status: "Overdue" })} testid="eq-stat-overdue" />
        <Stat label="Out of Service" value={dashboard?.counts?.["Out of Service"] ?? 0} tone="slate" onClick={() => setFilters({ ...filters, status: "Out of Service" })} testid="eq-stat-oos" />
      </div>

      {/* Requirement-type breakdown */}
      <Card className="glass-card mb-4"><CardContent className="p-4">
        <div className="text-sm font-semibold mb-2">By requirement type</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          {REQ_TYPES.map(t => {
            const b = dashboard?.by_requirement?.[t] || { due_soon: 0, overdue: 0 };
            return (
              <button key={t} onClick={() => setFilters({ ...filters, requirement_type: t, status: "all" })} data-testid={`eq-reqfilter-${t}`}
                      className="rounded-lg border p-2 text-left hover:border-indigo-300 transition">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 truncate">{t}</div>
                <div className="mt-0.5 flex items-center gap-2 text-sm">
                  <span className="text-amber-700 font-semibold">{b.due_soon}</span><span className="text-slate-300">·</span>
                  <span className="text-rose-700 font-semibold">{b.overdue}</span>
                </div>
                <div className="text-[9px] text-slate-400">Due · Over</div>
              </button>
            );
          })}
        </div>
      </CardContent></Card>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3 items-center">
        <span className="text-xs text-slate-500 mr-1">Filters:</span>
        <Select value={filters.status} onValueChange={v => setFilters({ ...filters, status: v })}>
          <SelectTrigger className="h-8 w-40 text-xs" data-testid="eq-filter-status"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{["Current", "Due Soon", "Overdue", "Out of Service"].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={filters.requirement_type} onValueChange={v => setFilters({ ...filters, requirement_type: v })}>
          <SelectTrigger className="h-8 w-44 text-xs" data-testid="eq-filter-req"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All requirements</SelectItem>{REQ_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
        </Select>
        {types.length > 0 && (
          <Select value={filters.equipment_type} onValueChange={v => setFilters({ ...filters, equipment_type: v })}>
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All types</SelectItem>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {depts.length > 0 && (
          <Select value={filters.department} onValueChange={v => setFilters({ ...filters, department: v })}>
            <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All departments</SelectItem>{depts.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {/* Register table */}
      <Card className="glass-card"><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 border-b"><tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="py-2 px-3">Asset</th><th className="py-2 px-3">Asset #</th><th className="py-2 px-3">Serial #</th>
              <th className="py-2 px-3">Type</th><th className="py-2 px-3">Dept / Location</th><th className="py-2 px-3">Responsible</th>
              <th className="py-2 px-3">Next due</th><th className="py-2 px-3">Status</th><th className="py-2 px-3"></th>
            </tr></thead>
            <tbody>
              {filtered.map(e => {
                const soonest = (e.requirements || []).map(r => r.next_due_date).filter(Boolean).sort()[0];
                return (
                  <tr key={e.id} data-testid={`eq-row-${e.id}`} className="border-b hover:bg-slate-50/60 cursor-pointer" onClick={() => setOpenDetail(e)}>
                    <td className="py-2 px-3 font-medium">{e.asset_name}<div className="text-[10px] text-slate-400">{(e.requirements || []).map(r => r.requirement_type).join(" · ") || "—"}</div></td>
                    <td className="py-2 px-3 text-xs font-mono">{e.asset_number || "—"}</td>
                    <td className="py-2 px-3 text-xs font-mono">{e.serial_number || "—"}</td>
                    <td className="py-2 px-3">{e.equipment_type || "—"}</td>
                    <td className="py-2 px-3 text-xs">{e.department || "—"}<div className="text-slate-400">{e.location || ""}</div></td>
                    <td className="py-2 px-3">{e.responsible_person || "—"}</td>
                    <td className="py-2 px-3 text-xs">{fmt(soonest)}</td>
                    <td className="py-2 px-3"><Badge className={STATUS_TONE[e.status]}>{e.status}</Badge></td>
                    <td className="py-2 px-3"><ChevronRight className="h-4 w-4 text-slate-400" /></td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-slate-500">No equipment matches the current filters.</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent></Card>

      <AddEquipmentModal open={openAdd} onOpenChange={setOpenAdd} clientSlug={clientSlug} onAdded={load} />
      <RecordActivityModal open={openRecord} onOpenChange={setOpenRecord} prefill={prefillRecord} onSaved={() => { load(); if (openDetail) setOpenDetail(equipment.find(x => x.id === openDetail.id) || openDetail); }} />
      <EquipmentDetailModal
        equipment={openDetail}
        open={!!openDetail}
        onOpenChange={o => !o && setOpenDetail(null)}
        onRecord={openRecordFor}
        onChanged={load}
      />
    </div>
  );
}

function Stat({ label, value, tone, onClick, testid }) {
  const c = { indigo: "text-indigo-600", emerald: "text-emerald-600", amber: "text-amber-600", rose: "text-rose-600", slate: "text-slate-600" }[tone] || "text-slate-700";
  return (
    <button onClick={onClick} data-testid={testid} className="text-left rounded-xl border bg-white p-3 hover:border-indigo-300 hover:shadow-sm transition">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`text-2xl font-extrabold mt-0.5 ${c}`}>{value}</div>
    </button>
  );
}

function AddEquipmentModal({ open, onOpenChange, clientSlug, onAdded }) {
  const empty = { asset_name: "", asset_number: "", serial_number: "", equipment_type: "", manufacturer: "", model: "", department: "", location: "", responsible_person: "", condition: "Good", notes: "" };
  const [form, setForm] = useState(empty);
  useEffect(() => { if (open) setForm(empty); /* eslint-disable-next-line */ }, [open]);
  const submit = async () => {
    if (!form.asset_name) { toast.error("Asset name required"); return; }
    try { await apiPost("/equipment", { client_slug: clientSlug, ...form }); toast.success("Equipment added"); onAdded?.(); onOpenChange(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="eq-add-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Wrench className="h-4 w-4 text-indigo-600" /> Add equipment</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Asset name *"><Input data-testid="eq-add-name" value={form.asset_name} onChange={e => setForm({ ...form, asset_name: e.target.value })} /></Field>
          <Field label="Asset number"><Input data-testid="eq-add-asset-num" value={form.asset_number} onChange={e => setForm({ ...form, asset_number: e.target.value })} /></Field>
          <Field label="Serial number"><Input value={form.serial_number} onChange={e => setForm({ ...form, serial_number: e.target.value })} /></Field>
          <Field label="Equipment type"><Input value={form.equipment_type} onChange={e => setForm({ ...form, equipment_type: e.target.value })} placeholder="e.g. Forklift, Torque wrench" /></Field>
          <Field label="Manufacturer"><Input value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} /></Field>
          <Field label="Model"><Input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} /></Field>
          <Field label="Department"><Input value={form.department} onChange={e => setForm({ ...form, department: e.target.value })} /></Field>
          <Field label="Location"><Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} /></Field>
          <Field label="Responsible person"><Input value={form.responsible_person} onChange={e => setForm({ ...form, responsible_person: e.target.value })} /></Field>
          <Field label="Condition">
            <Select value={form.condition} onValueChange={v => setForm({ ...form, condition: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["New", "Good", "Fair", "Needs repair"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></Field>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button data-testid="eq-add-save" className="brand-gradient text-white" onClick={submit}>Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EquipmentDetailModal({ equipment, open, onOpenChange, onRecord, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [records, setRecords] = useState([]);
  const [addReqOpen, setAddReqOpen] = useState(false);

  useEffect(() => {
    if (!equipment) return;
    apiGet(`/equipment/${equipment.id}`).then(setDetail);
    apiGet(`/equipment/${equipment.id}/records`).then(setRecords).catch(() => setRecords([]));
  }, [equipment]);
  if (!equipment) return null;
  const d = detail || equipment;

  const removeReq = async (rid) => {
    try { const r = await api.delete(`/equipment/${d.id}/requirements/${rid}`); setDetail(r.data); onChanged?.(); toast.success("Requirement removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setOOS = async () => {
    const status_override = d.status_override === "Out of Service" ? null : "Out of Service";
    try { const r = await api.patch(`/equipment/${d.id}`, { status_override }); setDetail(r.data); onChanged?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="eq-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wrench className="h-5 w-5 text-indigo-600" /> {d.asset_name}
            <Badge className={STATUS_TONE[d.status]}>{d.status}</Badge></DialogTitle>
          <DialogDescription>{d.equipment_type || "Equipment"} · {d.manufacturer} {d.model} · {d.department} / {d.location}</DialogDescription>
        </DialogHeader>
        <Tabs defaultValue="requirements">
          <TabsList>
            <TabsTrigger value="requirements">Requirements</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <TabsContent value="requirements" className="pt-3 space-y-2">
            <div className="flex justify-between items-center">
              <div className="text-xs text-slate-500">One equipment item can have multiple requirements — add them below.</div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={() => setAddReqOpen(true)} data-testid="eq-add-req"><Plus className="h-4 w-4 mr-1" /> Add requirement</Button>
                {REQ_TYPES.slice(0, 4).map(t => (
                  <Button key={t} size="sm" variant="ghost" className="text-xs" onClick={() => onRecord(d, t)} data-testid={`eq-quick-${t}`}>
                    <FileUp className="h-3 w-3 mr-1" /> Record {t}
                  </Button>
                ))}
              </div>
            </div>
            <div className="rounded-lg border bg-white divide-y">
              {(d.requirements || []).length === 0 && <div className="text-xs text-slate-400 text-center py-6">No requirements yet — add Calibration, Load Test, Service or others.</div>}
              {(d.requirements || []).map(r => (
                <div key={r.id} className="p-3 flex items-center gap-3">
                  <div className="flex-1">
                    <div className="text-sm font-semibold flex items-center gap-2">{r.requirement_type} <Badge className={STATUS_TONE[r.status || "Current"]}>{r.status}</Badge></div>
                    <div className="text-xs text-slate-500">Every {r.frequency_months} mo · Last {fmt(r.last_completed_date)} · Next {fmt(r.next_due_date)} · {r.service_provider || "—"}</div>
                  </div>
                  <Button size="sm" onClick={() => onRecord(d, r.requirement_type)} data-testid={`eq-record-${r.id}`}><FileUp className="h-4 w-4 mr-1" /> Record</Button>
                  <Button size="sm" variant="ghost" onClick={() => removeReq(r.id)} data-testid={`eq-remove-req-${r.id}`}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                </div>
              ))}
            </div>
            <AddRequirementModal open={addReqOpen} onOpenChange={setAddReqOpen} equipmentId={d.id} onAdded={(updated) => { setDetail(updated); onChanged?.(); }} />
          </TabsContent>

          <TabsContent value="details" className="pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <KV label="Asset number">{d.asset_number || "—"}</KV>
            <KV label="Serial number">{d.serial_number || "—"}</KV>
            <KV label="Manufacturer">{d.manufacturer || "—"}</KV>
            <KV label="Model">{d.model || "—"}</KV>
            <KV label="Department">{d.department || "—"}</KV>
            <KV label="Location">{d.location || "—"}</KV>
            <KV label="Responsible">{d.responsible_person || "—"}</KV>
            <KV label="Condition">{d.condition || "—"}</KV>
            <div className="sm:col-span-2"><KV label="Notes">{d.notes || "—"}</KV></div>
            <div className="sm:col-span-2 pt-2">
              <Button variant="outline" onClick={setOOS} data-testid="eq-toggle-oos">
                <PowerOff className="h-4 w-4 mr-1" /> {d.status_override === "Out of Service" ? "Return to service" : "Mark Out of Service"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="history" className="pt-3">
            <div className="space-y-2 max-h-[420px] overflow-y-auto">
              {records.length === 0 && <div className="text-xs text-slate-400 text-center py-6">No records yet — upload certificates as jobs are completed.</div>}
              {records.map(r => (
                <div key={r.id} className="rounded-lg border p-3 bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold">{r.requirement_type} — {fmt(r.performed_date)}</div>
                      <div className="text-xs text-slate-500">By {r.performed_by || r.uploaded_by} · {r.service_provider || "—"}</div>
                    </div>
                    {r.certificate?.filename && (
                      <a href={`${API}/equipment-records/${r.id}/file`} target="_blank" rel="noreferrer"
                         className="text-indigo-600 text-xs font-semibold hover:underline">↓ {r.certificate.filename}</a>
                    )}
                  </div>
                  {r.next_due_date && <div className="text-xs text-slate-500 mt-1">Next due: <span className="font-mono">{r.next_due_date}</span></div>}
                  {r.findings && <div className="text-xs mt-1 italic text-slate-600">"{r.findings}"</div>}
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function AddRequirementModal({ open, onOpenChange, equipmentId, onAdded }) {
  const [form, setForm] = useState({ requirement_type: "Calibration", frequency_months: 12, next_due_date: "", service_provider: "", reminder_days: 30 });
  useEffect(() => { if (open) setForm({ requirement_type: "Calibration", frequency_months: 12, next_due_date: "", service_provider: "", reminder_days: 30 }); }, [open]);
  const submit = async () => {
    try { const r = await apiPost(`/equipment/${equipmentId}/requirements`, form); onAdded?.(r); onOpenChange(false); toast.success("Requirement added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Add failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="eq-add-req-modal">
        <DialogHeader><DialogTitle>Add requirement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Type">
            <Select value={form.requirement_type} onValueChange={v => setForm({ ...form, requirement_type: v })}>
              <SelectTrigger data-testid="eq-req-type"><SelectValue /></SelectTrigger>
              <SelectContent>{REQ_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Frequency (months)"><Input type="number" min={1} value={form.frequency_months} onChange={e => setForm({ ...form, frequency_months: Number(e.target.value) })} /></Field>
            <Field label="Reminder (days before)"><Input type="number" min={1} value={form.reminder_days} onChange={e => setForm({ ...form, reminder_days: Number(e.target.value) })} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Next due date"><Input type="date" value={form.next_due_date} onChange={e => setForm({ ...form, next_due_date: e.target.value })} data-testid="eq-req-nextdue" /></Field>
            <Field label="Service provider"><Input value={form.service_provider} onChange={e => setForm({ ...form, service_provider: e.target.value })} /></Field>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button data-testid="eq-req-save" onClick={submit} className="brand-gradient text-white">Add</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RecordActivityModal({ open, onOpenChange, prefill, onSaved }) {
  const { eq, requirement_type } = prefill || {};
  const [form, setForm] = useState({ performed_date: new Date().toISOString().slice(0, 10), performed_by: "", service_provider: "", findings: "", notes: "", next_due_date: "" });
  const [file, setFile] = useState(null);
  useEffect(() => { if (open) { setForm({ performed_date: new Date().toISOString().slice(0, 10), performed_by: "", service_provider: "", findings: "", notes: "", next_due_date: "" }); setFile(null); } }, [open]);
  if (!eq) return null;
  const req = (eq.requirements || []).find(r => r.requirement_type === requirement_type);
  const submit = async () => {
    const fd = new FormData();
    if (req) fd.append("requirement_id", req.id);
    fd.append("requirement_type", requirement_type || (req?.requirement_type || "Service"));
    fd.append("performed_date", form.performed_date);
    fd.append("performed_by", form.performed_by);
    fd.append("service_provider", form.service_provider || (req?.service_provider || ""));
    fd.append("findings", form.findings);
    fd.append("notes", form.notes);
    if (form.next_due_date) fd.append("next_due_date", form.next_due_date);
    if (file) fd.append("file", file);
    try { await apiPostForm(`/equipment/${eq.id}/records`, fd); toast.success("Recorded"); onSaved?.(); onOpenChange(false); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="eq-record-modal">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><FileUp className="h-4 w-4 text-indigo-600" /> Record {requirement_type} — {eq.asset_name}</DialogTitle>
          <DialogDescription>Previous records are kept in history — this creates a new entry.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Performed date"><Input type="date" data-testid="eq-rec-date" value={form.performed_date} onChange={e => setForm({ ...form, performed_date: e.target.value })} /></Field>
            <Field label="Next due (auto)"><Input type="date" value={form.next_due_date} onChange={e => setForm({ ...form, next_due_date: e.target.value })} placeholder="auto from frequency" /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Performed by"><Input value={form.performed_by} onChange={e => setForm({ ...form, performed_by: e.target.value })} /></Field>
            <Field label="Service provider"><Input value={form.service_provider} onChange={e => setForm({ ...form, service_provider: e.target.value })} /></Field>
          </div>
          <Field label="Findings"><Textarea rows={2} value={form.findings} onChange={e => setForm({ ...form, findings: e.target.value })} /></Field>
          <Field label="Certificate / report file (optional, ≤20 MB)">
            <input data-testid="eq-rec-file" type="file" onChange={e => setFile(e.target.files?.[0] || null)} className="block text-sm" />
            {file && <div className="text-xs text-slate-500 mt-1">Selected: {file.name} ({(file.size / 1024).toFixed(0)} KB)</div>}
          </Field>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button><Button data-testid="eq-rec-save" onClick={submit} className="brand-gradient text-white">Save record</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) { return <div><Label className="text-xs">{label}</Label>{children}</div>; }
function KV({ label, children }) { return <div><div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div><div className="text-sm mt-1">{children}</div></div>; }
